import React from "react";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Play, Pause, Square, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import LiveTimer from "./components/LiveTimer";
import CheckoutDialog from "./components/CheckoutDialog";
import QCQuickCheckDialog from "./components/QCQuickCheckDialog";
import { useMesStore } from "@/stores/useMesStore";

export default function MesSessionPage() {
  const { id } = useParams({ strict: false });
  const navigate = useNavigate();
  const { setManualAddOpen, setActiveSessionLogId } = useMesStore();
  const [isQcOpen, setIsQcOpen] = React.useState(false);

  const queryClient = useQueryClient();

  const {
    data: log,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: ["mesSession", id],
    queryFn: () =>
      trpcClient.mes.listLogs
        .query({
          routingStepId: id as string,
        })
        .then((logs: any) => logs.find((l: any) => l.id === id) || null),
  });

  const pauseMutation = useMutation({
    mutationFn: (variables: any) =>
      trpcClient.mes.pauseSession.mutate(variables),
    onSuccess: () => {
      toast.success("Đã tạm dừng");
      refetch();
    },
    onError: (err: any) => toast.error(err.message),
  });

  const resumeMutation = useMutation({
    mutationFn: (variables: any) =>
      trpcClient.mes.resumeSession.mutate(variables),
    onSuccess: () => {
      toast.success("Đã tiếp tục");
      refetch();
    },
    onError: (err: any) => toast.error(err.message),
  });

  if (isLoading)
    return (
      <div className="p-8 text-center text-slate-500">
        Đang tải thông tin phiên làm việc...
      </div>
    );
  if (!log)
    return (
      <div className="p-8 text-center text-red-500">
        Không tìm thấy thông tin phiên làm việc!
      </div>
    );

    return (
    <StandardDetailLayout
      title={`${log.productionOrder?.orderId || id}`}
      breadcrumbs={[
        { label: "Điều hành MES", to: "/mes" },
        { label: log.productionOrder?.orderId || "Chi tiết phiên" },
      ]}
    >
      <div className="flex flex-col w-full h-full p-4 space-y-6"><div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="md:col-span-2 border-slate-200">
          <CardHeader>
            <CardTitle>Thông tin Lệnh Sản Xuất</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex flex-col">
                <span className="text-slate-500">Trạng thái Phiên</span>
                <span className="font-semibold">{log.status}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-slate-500">Máy</span>
                <span className="font-semibold">
                  {log.machine?.name || "—"}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-slate-500">Người vận hành</span>
                <span className="font-semibold">
                  {log.operator?.fullName || "—"}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-slate-500">Thời gian chạy</span>
                <span className="font-semibold text-lg text-indigo-600">
                  <LiveTimer
                    startTime={log.runStartTime}
                    accumulatedSeconds={log.accumulatedSeconds || 0}
                    isRunning={log.status === "RUNNING"}
                  />
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle>Thao Tác Nhanh</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {log.status === "RUNNING" ? (
              <Button
                size="lg"
                variant="outline"
                className="w-full h-16 text-lg justify-start"
                onClick={() => pauseMutation.mutate({ logId: log.id })}
                disabled={pauseMutation.isPending}
              >
                <Pause className="w-6 h-6 mr-4 text-amber-500" /> Tạm dừng
              </Button>
            ) : log.status === "PAUSED" ? (
              <Button
                size="lg"
                variant="outline"
                className="w-full h-16 text-lg justify-start border-green-200 bg-green-50"
                onClick={() => resumeMutation.mutate({ logId: log.id })}
                disabled={resumeMutation.isPending}
              >
                <Play className="w-6 h-6 mr-4 text-green-600" /> Tiếp tục
              </Button>
            ) : (
              <div className="p-4 bg-slate-50 text-slate-500 text-center rounded-md">
                Phiên đã kết thúc
              </div>
            )}

            {log.status !== "COMPLETED" && (
              <Button
                size="lg"
                className="w-full h-16 text-lg justify-start bg-indigo-600 hover:bg-indigo-700"
                onClick={() => {
                  setActiveSessionLogId(log.id);
                  setManualAddOpen(true);
                }}
              >
                <Square className="w-6 h-6 mr-4 text-white" /> Báo cáo /
                Checkout
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <Card className="border-dashed border-slate-300 bg-slate-50 h-64 flex flex-col items-center justify-center space-y-4">
          <div className="text-slate-400 text-center">
            <p className="font-medium mb-1">Kiểm tra Chất lượng (QC)</p>
            <p className="text-sm">Ghi nhận thông số nhanh tại máy</p>
          </div>
          <Button variant="outline" onClick={() => setIsQcOpen(true)}>
            Nhập QC nhanh
          </Button>
        </Card>
        <Card className="border-dashed border-slate-300 bg-slate-50 h-64 flex flex-col items-center justify-center space-y-4">
          <div className="text-slate-400 text-center">
            <p className="font-medium mb-1">Vật tư (BOM)</p>
            <p className="text-sm">Vật tư sẽ được khấu trừ tự động (Backflushing) khi Checkout.</p>
          </div>
        </Card>
      </div>

      <CheckoutDialog isManual={false} />
      {log && (
        <QCQuickCheckDialog
          open={isQcOpen}
          onOpenChange={setIsQcOpen}
          routingStepId={log.routingStepId}
        />
      )}
      </div>
    </StandardDetailLayout>
  );
}
